import { PhysicsObject, SimulationState } from '@/types/simulation';

interface Props {
  selectedObject: PhysicsObject | null;
  simState: SimulationState;
  objects: PhysicsObject[];
  isDark?: boolean;
}

function StatRow({ label, value, unit = '', isDark = true, highlight = false }: {
  label: string; value: string; unit?: string; isDark?: boolean; highlight?: boolean;
}) {
  const textMuted   = isDark ? 'text-white/35' : 'text-gray-500';
  const borderColor = isDark ? 'border-white/5' : 'border-black/5';
  const accentColor = highlight
    ? (isDark ? 'text-amber-300' : 'text-amber-600')
    : (isDark ? 'text-cyan-300' : 'text-sky-600');
  const unitColor   = isDark ? 'text-white/25' : 'text-gray-400';

  return (
    <div className={`flex items-center justify-between py-1.5 border-b ${borderColor} last:border-0`}>
      <span className={`text-xs ${textMuted}`}>{label}</span>
      <span className={`text-xs font-mono ${accentColor}`}>
        {value}
        {unit && <span className={`${unitColor} ml-0.5`}>{unit}</span>}
      </span>
    </div>
  );
}

// Mini sparkline-style velocity bar
function VelocityBar({ speed, maxSpeed = 20, color, isDark = true }: {
  speed: number; maxSpeed?: number; color: string; isDark?: boolean;
}) {
  const pct = Math.min(speed / maxSpeed, 1);
  const bgTrack = isDark ? 'bg-white/8' : 'bg-black/8';
  return (
    <div className={`w-full h-1.5 rounded-full ${bgTrack} overflow-hidden`}>
      <div
        className="h-full rounded-full transition-all duration-100"
        style={{ width: `${pct * 100}%`, backgroundColor: color, boxShadow: `0 0 6px ${color}80` }}
      />
    </div>
  );
}

export default function ResultsPanel({ selectedObject, simState, objects, isDark = true }: Props) {
  const obj = selectedObject;
  const speed = obj ? Math.sqrt(obj.vx * obj.vx + obj.vy * obj.vy) : 0;
  const ke = obj ? 0.5 * obj.mass * speed * speed : 0;
  // Potential energy relative to y=0 (approximate)
  const pe = obj ? obj.mass * simState.gravity * obj.y : 0;

  const fmt = (n: number, d = 3) =>
    Math.abs(n) > 9999 ? n.toExponential(2) : n.toFixed(d);

  const textMuted     = isDark ? 'text-white/35' : 'text-gray-500';
  const textPrimary   = isDark ? 'text-white'    : 'text-gray-900';
  const textDim       = isDark ? 'text-white/25' : 'text-gray-400';
  const accentTime    = isDark ? 'text-white'    : 'text-gray-900';
  const accentTimeUnit= isDark ? 'text-white/30' : 'text-gray-400';
  const accentObjects = isDark ? 'text-yellow-300' : 'text-amber-600';
  const borderSubtle  = isDark ? 'border-white/6' : 'border-black/6';

  return (
    <div className="space-y-3 animate-float-in">
      <h2 className="section-label flex items-center gap-2">
        <div className="w-4 h-4 flex items-center justify-center">
          <i className={`ri-line-chart-line ${isDark ? 'text-pink-400' : 'text-pink-500'}`} />
        </div>
        計測結果
      </h2>

      {/* Time + object count */}
      <div className="glass rounded-xl p-3">
        <div className="flex items-center justify-between">
          <span className={`text-xs ${textMuted}`}>経過時間</span>
          <span className={`text-2xl font-mono font-bold ${accentTime} ${isDark ? 'glow-cyan' : ''}`}>
            {simState.time.toFixed(2)}
            <span className={`text-xs ${accentTimeUnit} ml-1 font-normal`}>s</span>
          </span>
        </div>
        <div className="flex items-center justify-between mt-1">
          <span className={`text-xs ${textDim}`}>物体数</span>
          <span className={`text-xs font-mono ${accentObjects}`}>{objects.length}</span>
        </div>
      </div>

      {/* Selected object detail */}
      {obj ? (
        <div className="glass rounded-xl p-3 space-y-1 animate-float-in">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <p className={`text-xs ${textMuted} flex items-center gap-1.5`}>
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: obj.color }} />
              選択中の物体
            </p>
            <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${isDark ? 'bg-white/6 text-white/40' : 'bg-black/5 text-gray-500'}`}>
              {obj.shape === 'circle' ? '球' : obj.shape === 'square' ? '箱' : '三角'}
            </span>
          </div>

          {/* Speed bar */}
          <div className="mb-2 space-y-1">
            <div className="flex justify-between items-center">
              <span className={`text-xs ${textMuted}`}>速さ |v|</span>
              <span className={`text-xs font-mono font-bold ${isDark ? 'text-amber-300' : 'text-amber-600'}`}>
                {fmt(speed, 2)}<span className={`${textDim} ml-0.5 font-normal`}>m/s</span>
              </span>
            </div>
            <VelocityBar speed={speed} color={obj.color} isDark={isDark} />
          </div>

          <div className={`border-t ${borderSubtle} pt-1`}>
            <StatRow label="位置 X"        value={fmt(obj.x)}   unit=" u"    isDark={isDark} />
            <StatRow label="位置 Y"        value={fmt(obj.y)}   unit=" u"    isDark={isDark} />
            <StatRow label="速度 Vx"       value={fmt(obj.vx)}  unit=" m/s"  isDark={isDark} />
            <StatRow label="速度 Vy"       value={fmt(obj.vy)}  unit=" m/s"  isDark={isDark} />
            <StatRow label="運動エネルギー" value={fmt(ke, 2)}   unit=" J"    isDark={isDark} highlight />
            <StatRow label="位置エネルギー" value={fmt(pe, 2)}   unit=" J"    isDark={isDark} />
            <StatRow label="力学的エネルギー" value={fmt(ke + pe, 2)} unit=" J" isDark={isDark} highlight />
            <StatRow label="質量"           value={fmt(obj.mass, 2)} unit=" kg" isDark={isDark} />
            <StatRow label="回転角"         value={fmt((obj.angle * 180) / Math.PI, 1)} unit="°" isDark={isDark} />
          </div>
        </div>
      ) : (
        <div className="glass rounded-xl py-8 text-center">
          <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
            <i className={`ri-information-line text-2xl ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
          </div>
          <p className={`text-xs ${textDim}`}>物体を選択するとリアルタイムデータを表示</p>
        </div>
      )}

      {/* All objects mini list */}
      {objects.length > 1 && (
        <div className="space-y-1">
          <p className="section-label">全物体</p>
          {objects.map((o, idx) => {
            const s = Math.sqrt(o.vx * o.vx + o.vy * o.vy);
            const isSelected = o.id === obj?.id;
            return (
              <div
                key={o.id}
                className={`glass rounded-lg overflow-hidden transition-all ${isSelected ? (isDark ? 'ring-1 ring-white/15' : 'ring-1 ring-black/10') : ''}`}
              >
                <div className="flex items-center gap-2 py-1.5 px-2.5">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: o.color }} />
                  <span className={`text-xs ${textMuted} flex-shrink-0`}>#{idx + 1}</span>
                  <span className={`text-xs font-mono ${textDim} flex-1 truncate`}>
                    ({o.x.toFixed(1)}, {o.y.toFixed(1)})
                  </span>
                  <span className={`text-xs font-mono font-semibold ${isDark ? 'text-cyan-400' : 'text-sky-600'} flex-shrink-0`}>
                    {s.toFixed(1)}<span className={`${textDim} font-normal`}>m/s</span>
                  </span>
                </div>
                <VelocityBar speed={s} color={o.color} isDark={isDark} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
